import {
  animationsConfig,
  cameraConfig,
  gameConfig
} from '@/app/config'
import {
  DisciplinePhases,
  PlayerAnimationsNames,
  PlayerStates,
  TutorialObjectiveIds
} from '@/app/types'
import {
  CallbackAnimationTypes,
  cameraManager,
  corePhasesManager,
  game,
  gsap,
  modes,
  PlayersSortTypes,
  THREE,
  trainingManager
} from '@powerplay/core-minigames'
import type { Athlete } from './Athlete'
import { audioHelper } from '@/app/audioHelper/AudioHelper'
import { disciplinePhasesManager } from '@/app/phases/DisciplinePhasesManager'
import { player } from './player'
import { playersManager } from '@powerplay/core-minigames'
import { tutorialObjectives } from '@/app/modes/tutorial/TutorialObjectives'

/**
 * Dedikovany manazer animacii pre hraca/superov.
 */
export class AthleteAnimationManager {

  /** ci sme uz nastavili podla stavov veci */
  private statesActive = {
    [PlayerStates.prepare]: true,
    [PlayerStates.run]: false,
    [PlayerStates.draw]: false,
    [PlayerStates.throw]: false,
    [PlayerStates.emotion]: false,
    [PlayerStates.runStop]: false
  }

  /** aktualna animacia - TODO rozsir o vsetky animacie mimo konecnej */
  public actualAnimation?: PlayerAnimationsNames

  /** ci sme nastavili konecnu animaciu */
  public isEndEmotionSet = false

  /** Tween pre animaciu slow down run */
  public tweenSlowDownRun?: gsap.core.Tween

  /** emocia hraca */
  public emotion = PlayerAnimationsNames.happy1

  /** tween pre emocie */
  private tweenEmotion?: gsap.core.Tween

  /** ci je emocia skipovatelna akurat */
  private emotionSkippable = false

  /** buduca mozna animacia kvoli crossfadeu */
  private possibleNextAnimation?: PlayerAnimationsNames

  /** Prepare animacia */
  private prepareAnimation?: PlayerAnimationsNames

  /** Pocitadlo pre zvuky krokov */
  private footstepsAudioCounter = 0

  /** Ktory index sa zahral naposledy */
  private lastFootstepsAudioIndex = 0

  /** Pole moznych cisel pre zvuky krokov */
  private footstepsAudioNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]

  /** ktora animacia bude */
  private throwAnimation = PlayerAnimationsNames.throw1

  /** kolko framov preslo v animacii throw */
  private throwAnimationFrames = 0

  /** aky bol posledny najlepsi pokus */
  private lastBestResult = -1

  /** Animacie, ktore su bezecke, pri ktorych davame zvuk krokov */
  private readonly runningAnimations = [
    PlayerAnimationsNames.happy1,
    PlayerAnimationsNames.happy2,
    PlayerAnimationsNames.happy3,
    PlayerAnimationsNames.intro1,
    PlayerAnimationsNames.intro2,
    PlayerAnimationsNames.intro3,
    PlayerAnimationsNames.intro4,
    PlayerAnimationsNames.intro4_1,
    PlayerAnimationsNames.neutral1,
    PlayerAnimationsNames.neutral2,
    PlayerAnimationsNames.prepare,
    PlayerAnimationsNames.run,
    PlayerAnimationsNames.throw1,
    PlayerAnimationsNames.throw2,
    PlayerAnimationsNames.throw3,
    PlayerAnimationsNames.windUp
  ]

  /**
   * Konstruktor
   * @param athlete - Atlet
   */
  public constructor(private athlete: Athlete) {

    this.shuffleFootstepsAudioNumbers()

  }

  /**
   * Zmena rychlosti animacii
   * @param speed - Nova rychlost animacii
   */
  private changeAnimationSpeed(speed: number) {

    this.athlete.animationsManager.setSpeed(speed)

  }

  /**
   * Reset rychlosti animacii
   */
  private resetAnimationSpeed() {

    this.athlete.animationsManager.resetSpeed()

  }

  /**
   * Poprehadzovanie poradia zvukov krokov, aby sa neopakovali
   */
  private shuffleFootstepsAudioNumbers(): void {

    function shuffle(array: number[]) {

      let currentIndex = array.length

      // While there remain elements to shuffle...
      while (currentIndex != 0) {

        // Pick a remaining element...
        const randomIndex = Math.floor(Math.random() * currentIndex)
        currentIndex--;

        // And swap it with the current element.
        [array[currentIndex], array[randomIndex]] = [
          array[randomIndex], array[currentIndex]]

      }

    }

    shuffle(this.footstepsAudioNumbers)

  }

  /**
   * Riesenie veci pre run stav
   * @returns True, ak ide o dany stav
   */
  private isRunState(): boolean {

    const isRun = this.athlete.isState(PlayerStates.run)

    if (!this.statesActive[PlayerStates.run] && isRun) {

      this.statesActive[PlayerStates.run] = true

      this.athlete.animationsManager.crossfadeTo(PlayerAnimationsNames.run, 0.2, true, true)


    }

    return isRun

  }

  /**
   * Riesenie veci pre run stop stav
   * @returns True, ak ide o dany stav
   */
  private isRunStopState(): boolean {

    const isRunStop = this.athlete.isState(PlayerStates.runStop)

    if (!this.statesActive[PlayerStates.runStop] && isRunStop) {

      this.statesActive[PlayerStates.runStop] = true

      this.athlete.animationsManager.changeTo(PlayerAnimationsNames.runStop)


    }

    return isRunStop

  }

  /**
   * Riesenie veci pre draw stav
   * @returns True, ak ide o dany stav
   */
  private isDrawState(): boolean {

    const isDraw = this.athlete.isState(PlayerStates.draw)

    if (!this.statesActive[PlayerStates.draw] && isDraw) {

      this.statesActive[PlayerStates.draw] = true
      this.athlete.animationsManager.crossfadeTo(PlayerAnimationsNames.windUp, 0.2, true, true)


    }

    return isDraw

  }

  /**
   * Riesenie veci pre draw stav
   * @returns True, ak ide o dany stav
   */
  private isThrowState(): boolean {

    const isThrow = this.athlete.isState(PlayerStates.throw)

    if (!this.statesActive[PlayerStates.throw] && isThrow) {

      this.statesActive[PlayerStates.throw] = true

      const rand = THREE.MathUtils.randInt(1, 3)
      this.throwAnimation = PlayerAnimationsNames.throw3
      if (rand === 1) this.throwAnimation = PlayerAnimationsNames.throw1
      if (rand === 2) this.throwAnimation = PlayerAnimationsNames.throw2

      this.athlete.slowDownAfterThrow()
      this.athlete.animationsManager.getAnimationAction(this.throwAnimation).time = 0
      this.athlete.animationsManager.getAnimationAction(this.throwAnimation).paused = false
      this.athlete.animationsManager.stopCrossfade(PlayerAnimationsNames.run, PlayerAnimationsNames.windUp)
      this.athlete.animationsManager.changeTo(PlayerAnimationsNames.windUp)
      this.athlete.animationsManager.changeTo(this.throwAnimation)

      gsap.to({}, {
        duration: gameConfig.delaySwitchCameraAfterThrown,
        onComplete: () => {

          if (player.wasFoul) {

            player.javelin.onEnd()
            return

          } else {

            tutorialObjectives.passObjective(TutorialObjectiveIds.validAttempt)

          }

          this.athlete.javelin.wasThrown = true

          // nastavime shake ostepu
          player.javelin.startShaking()

          // nastavime kameru
          cameraManager.changeIdeals(cameraConfig.javelinFlight.cameraOffset)

          // tiene podla ineho objektu
          game.shadowsManager.setObjectToFollowWithLight(player.javelin.javelinObjectFlying)

          // hlasky davame iba ked nebol preslap
          disciplinePhasesManager.phaseDraw.showMessageDraw()
          disciplinePhasesManager.phaseDraw.showMessageAngle()

        }
      })

    }

    return isThrow

  }

  private checkForThrow(): void {

    if (this.athlete.isState(PlayerStates.throw)) {

      if (
        !this.athlete.javelin.isFlying &&
        !this.athlete.javelin.wasThrown
      ) {

        if (this.throwAnimationFrames === 20) {

          disciplinePhasesManager.phaseDraw.calculateThrow()

        }
        this.throwAnimationFrames += 1

      }

    }

  }

  /**
   * Zistenie konkretnej animacie posledneho pokusu
   * @returns Animacia
   */
  private getEmotionLastAttempt(): PlayerAnimationsNames {

    let animation = PlayerAnimationsNames.happy1
    const position = playersManager.getPlayerActualPosition()

    if ((!modes.isEventBossFight() && position <= 3) || (modes.isEventBossFight() && position === 1)) {

      const random = THREE.MathUtils.randInt(1, 3)
      if (random === 1) animation = PlayerAnimationsNames.happy2
      if (random === 2) animation = PlayerAnimationsNames.happy3

    } else if ((!modes.isEventBossFight() && position >= 6) || (modes.isEventBossFight() && position > 1)) {

      const random = THREE.MathUtils.randInt(1, 2)
      if (random === 1) animation = PlayerAnimationsNames.sad1
      if (random === 2) animation = PlayerAnimationsNames.sad2

    } else {

      const random = THREE.MathUtils.randInt(1, 2)
      if (random === 1) animation = PlayerAnimationsNames.neutral1
      if (random === 2) animation = PlayerAnimationsNames.neutral2

    }

    return animation

  }

  /**
   * Zistenie konkretnej animacie
   * @returns Animacia
   */
  private getEmotion(): PlayerAnimationsNames {

    if (corePhasesManager.disciplineActualAttempt === corePhasesManager.disciplineAttemptsCount) {

      return this.getEmotionLastAttempt()

    }

    let animation = PlayerAnimationsNames.happy1
    const position = playersManager.getPlayerActualPosition()
    const resultsArr = playersManager.getPlayer().resultsArr
    const actualResult = resultsArr?.[corePhasesManager.disciplineActualAttempt - 1]?.main ?? -1
    const notImproved = actualResult <= this.lastBestResult

    if ((!notImproved && position <= 3) || this.athlete.pbThisAttempt) {

      const random = THREE.MathUtils.randInt(1, 3)
      if (random === 1) animation = PlayerAnimationsNames.happy2
      if (random === 2) animation = PlayerAnimationsNames.happy3

    } else if (notImproved && position > 3) {

      const random = THREE.MathUtils.randInt(1, 2)
      if (random === 1) animation = PlayerAnimationsNames.sad1
      if (random === 2) animation = PlayerAnimationsNames.sad2

    } else {

      const random = THREE.MathUtils.randInt(1, 2)
      if (random === 1) animation = PlayerAnimationsNames.neutral1
      if (random === 2) animation = PlayerAnimationsNames.neutral2

    }

    this.lastBestResult = playersManager.getBestResult(PlayersSortTypes.descending, undefined, true)

    return animation

  }

  /**
   * Vratenie konecne emocie v treningu
   * @returns Emocia
   */
  private getTrainingEndEmotion(): PlayerAnimationsNames {

    const tasks = trainingManager.getTrainingTasks()
    const sum = tasks.reduce((prev, current) => prev + current.value, 0)
    const average = sum / tasks.length

    // default je 0-2 hviezd
    let animation = PlayerAnimationsNames.happy1

    // ak 3 hviezdy
    if (average > 0.9) {

      const random = THREE.MathUtils.randInt(1, 3)
      if (random === 1) animation = PlayerAnimationsNames.happy2
      if (random === 2) animation = PlayerAnimationsNames.happy3

    } else {

      const random = THREE.MathUtils.randInt(1, 2)
      if (random === 1) animation = PlayerAnimationsNames.neutral1
      if (random === 2) animation = PlayerAnimationsNames.neutral2

    }

    return animation

  }

  public handleInputs(): void {

    if (!this.emotionSkippable) return

    this.tweenEmotion?.kill()
    this.onLoopEndEmotion()

  }

  /**
   * Co sa ma stat, ked skonci loop emocie
   */
  private onLoopEndEmotion(): void {

    this.emotionSkippable = false
    player.animationsManager.removeAnimationCallback(this.emotion, CallbackAnimationTypes.loop)

    // pokial nejde o posledny pokus, tak ukoncime
    disciplinePhasesManager.phaseFinish.afterEmotions(0)

  }

  /**
   * Riesenie veci pre emotion stav
   * @returns True, ak ide o dany stav
   */
  private isEmotionState(): boolean {

    const isEmotion = this.athlete.isState(PlayerStates.emotion)

    if (!this.statesActive[PlayerStates.emotion] && isEmotion) {

      this.statesActive[PlayerStates.emotion] = true
      this.emotion = modes.isTrainingMode() ? this.getTrainingEndEmotion() : this.getEmotion()

      // iba mimo posledneho pokusu davame ukoncenie pri prvom loope
      if (corePhasesManager.disciplineActualAttempt < corePhasesManager.disciplineAttemptsCount) {

        player.animationsManager.addAnimationCallback(this.emotion, CallbackAnimationTypes.loop, () => {

          this.onLoopEndEmotion()

        })

        this.tweenEmotion = gsap.to(
          {},
          {
            duration: 0.2,
            onComplete: () => {

              this.emotionSkippable = true

            }
          }
        )

      }

      player.animationsManager.crossfadeTo(this.emotion, 0.02, true, false)

    }

    return isEmotion

  }

  /**
   * Samotna logika
   */
  private animationLogic(): void {

    this.isRunStopState()
    this.isRunState()
    this.isDrawState()
    this.isThrowState()
    this.isEmotionState()

  }

  /**
   * Sprava zvukov krokov
   * @param animation - Animacia s ktorou sa aktualne pracuje
   */
  private manageFootstepsAudio(animation: PlayerAnimationsNames): void {

    const config = gameConfig.audioFootstepsTimes

    if (!config || config.length === 0) return

    const coefLeft = config[0]
    const coefRight = config[1]
    const animationAction = this.athlete.animationsManager.getAnimationAction(animation)
    const nearestLimit = this.footstepsAudioCounter % 2 === 0 ? coefLeft : coefRight
    const durationAnimation = animationAction.getClip().duration
    let time = animationAction.time

    if (!animationsConfig[animation].loop && time >= durationAnimation) return

    // ak sme nad druhym limitom, tak musime to akoby preniest do zapornych cisiel alebo pod prvy limit
    if (nearestLimit === coefLeft && time > coefRight) {

      time = time - durationAnimation

    }

    // bol dalsi doslap
    if (time > nearestLimit) {

      this.footstepsAudioCounter++

      audioHelper.playMovementAudio(this.footstepsAudioNumbers[this.lastFootstepsAudioIndex])
      this.lastFootstepsAudioIndex += 1
      if (this.lastFootstepsAudioIndex >= this.footstepsAudioNumbers.length) this.lastFootstepsAudioIndex = 0

    }

  }

  /**
   * Logika zvuku krokov
   */
  private footstepsAudioLogic(): void {

    // kroky davam iba ked je running faza
    if (!disciplinePhasesManager.oneOfPhaseIsActual([DisciplinePhases.running, DisciplinePhases.draw])) return

    this.actualAnimation = this.athlete.animationsManager.getActualAnimation() as PlayerAnimationsNames

    if (!this.actualAnimation) return

    const actualAnimationAction = this.athlete.animationsManager.getAnimationAction(this.actualAnimation)
    const actualAnimationWeight = actualAnimationAction.getEffectiveWeight()
    const animationToWorkWith = actualAnimationWeight >= 0.5 ? this.actualAnimation : this.possibleNextAnimation

    if (animationToWorkWith && this.runningAnimations.includes(animationToWorkWith)) {

      this.manageFootstepsAudio(animationToWorkWith)

    } else {

      this.footstepsAudioCounter = 0

    }

  }

  /**
   * Update metoda volana v move metode velocity manazera
   */
  public update(): void {

    this.animationLogic()
    this.footstepsAudioLogic()
    this.checkForThrow()

  }

  /**
   * Zresetovanie veci
   */
  public reset(): void {

    this.actualAnimation = undefined
    this.isEndEmotionSet = false
    this.statesActive = {
      [PlayerStates.prepare]: true,
      [PlayerStates.run]: false,
      [PlayerStates.draw]: false,
      [PlayerStates.throw]: false,
      [PlayerStates.emotion]: false,
      [PlayerStates.runStop]: false
    }

    this.footstepsAudioCounter = 0
    this.lastFootstepsAudioIndex = 0
    this.throwAnimationFrames = 0

  }

}
